/**
 * Leads arriving from Google Sheets / Facebook pack campaign metadata into the
 * single `comments` column as a pipe-separated string, e.g.
 *
 *   "⚠️ Πιθανό διπλότυπο — ίδιο τηλέφωνο (lead: 1a2b…) | Campaign: X | Form: Y | Organic: false"
 *
 * Rendering that raw is noisy — partners in particular only care about what the
 * customer actually wrote. These helpers split the blob into its parts so each
 * surface can show the bits it needs.
 *
 * Built by `receive-sheets-lead.php` (see $commentParts).
 */

export interface LeadCommentWarning {
  text: string;
  /** Present when the warning references another lead, so the UI can link to it. */
  linkedLeadId?: string;
}

export interface ParsedLeadComments {
  warnings: LeadCommentWarning[];
  campaign?: string;
  form?: string;
  ad?: string;
  organic?: boolean;
  /** What the customer actually wrote — everything that isn't metadata. */
  freeText: string;
}

export function parseLeadComments(raw: string | null | undefined): ParsedLeadComments {
  const result: ParsedLeadComments = { warnings: [], freeText: '' };
  if (!raw) return result;

  const free: string[] = [];
  const metadataOnly = /(?:^| \| )(?:Realtime Meta API(?: \||$)|(?:Campaign|Form|Ad(?: ID)?|Organic):)/i.test(raw.trim());
  let continuation: 'campaign' | 'form' | 'ad' | null = null;

  for (const part of raw.split(' | ').map((p) => p.trim()).filter(Boolean)) {
    if (part.startsWith('⚠️')) {
      continuation = null;
      const linked = part.match(/\(lead:\s*([0-9a-f-]{36})\)/i);
      result.warnings.push({
        text: part.replace(/^⚠️\s*/, ''),
        linkedLeadId: linked?.[1],
      });
    } else if (/^Campaign:\s*/i.test(part)) {
      result.campaign = part.replace(/^Campaign:\s*/i, '');
      continuation = 'campaign';
    } else if (/^Form:\s*/i.test(part)) {
      result.form = part.replace(/^Form:\s*/i, '');
      continuation = 'form';
    } else if (/^Ad(?: ID)?:\s*/i.test(part)) {
      result.ad = part.replace(/^Ad(?: ID)?:\s*/i, '');
      continuation = 'ad';
    } else if (/^Organic:\s*/i.test(part)) {
      result.organic = part.replace(/^Organic:\s*/i, '').toLowerCase() === 'true';
      continuation = null;
    } else if (part.toLowerCase() !== 'realtime meta api') {
      if (continuation) result[continuation] += ` | ${part}`;
      else if (!metadataOnly) free.push(part);
    }
  }

  result.freeText = free.join(' | ');
  return result;
}

/**
 * Just the customer's own text, with all campaign metadata stripped.
 * Returns '' when the comments held nothing but metadata.
 */
export function customerCommentText(raw: string | null | undefined): string {
  return parseLeadComments(raw).freeText;
}
